import { defineStore } from 'pinia';

import { GENERAL_addEditDelete } from '~/utils/useBackend';
import type { components } from '@/types_backend';

const URL = '/api/billing/';
const URL_billingPeriod = '/api/billing/billing-period/';
const URL_subscriptionType = '/api/billing/subscription-type/';

type DjangoResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export const useBillingStore = defineStore('billing', {
  state: () => ({
    currentBillingPeriod: null as
      | components['schemas']['BillingPeriodOutput']
      | null,
    subscriptionTypes: [] as components['schemas']['SubscriptionType'][],
  }),
  actions: {
    async initializeSubscriptionForUserAndCompany(initData: {
      user: components['schemas']['CustomUserCreate'];
      company: components['schemas']['CompanyInfo'];
      subscription_type: null | number;
    }) {
      if (!initData.subscription_type) return false;

      const data = await $fetch(`${URL}initialize-subscription/`, {
        method: 'POST',
        body: JSON.stringify(initData),
      });

      return data;
    },

    /* BillingPeriod */
    async getCurrentBillingPeriod() {
      const data = (await backendGetRequest(
        `${URL_billingPeriod}`,
        { page: 1, page_size: 1, sortBy: [] },
        {
          status: 'active',
        }
      )) as DjangoResponse<components['schemas']['BillingPeriodOutput']>;

      if (!data.results[0]) return null;

      this.currentBillingPeriod = data.results[0];

      return data;
    },
    async getBillingPeriodById(id: number) {
      const data = (await apiGetRequest(
        `${URL_billingPeriod}${id}/`
      )) as components['schemas']['BillingPeriodOutput'];

      return data;
    },
    async getBillingPeriods(
      {
        page = 1 as number,
        page_size = 10 as number,
        sortBy = [] as { key: string; order: 'asc' | 'desc' }[],
      },
      filter: any = {},
      search: string = ''
    ) {
      const data = (await backendGetRequest(
        URL_billingPeriod,
        { page, page_size, sortBy },
        filter,
        search
      )) as DjangoResponse<components['schemas']['BillingPeriodOutput']>;

      return data;
    },

    /* SubscriptionType */
    async getSubscriptionTypeById(id: number) {
      const data = (await apiGetRequest(
        `${URL_subscriptionType}${id}/`
      )) as any;

      return data;
    },
    async getSubscriptionTypes(): Promise<
      DjangoResponse<components['schemas']['SubscriptionType']>
    > {
      const data = (await backendGetRequest(
        URL_subscriptionType,
        { page: 1, page_size: 1000, sortBy: [] },
        {},
        ''
      )) as any;

      this.subscriptionTypes = data.results;

      return data;
    },

    // CRUD
    async cancelBillingPeriod(billingPeriodId: number) {
      await apiPutRequest(
        `${URL}cancel-billing-period/${billingPeriodId}/`,
        {}
      );
    },
    async changeCompanySubscription(
      futureSubscription: components['schemas']['SubscriptionType']
    ) {
      await apiPutRequest(`${URL}change-company-subscription/`, {
        new_subscription_type_id: futureSubscription.id,
      });
    },
    async increaseCurrentUserAmount(new_user_count: number) {
      // Check if new_user_count is a positive integer
      if (!Number.isInteger(new_user_count) || new_user_count <= 0) {
        throw new Error('New user count must be a positive integer.');
      }

      // Check if new user count is greater than current user count
      const currentUserCount = this.currentBillingPeriod?.amount_of_users || 0;

      if (new_user_count <= currentUserCount) {
        throw new Error(
          'New user count must be greater than current user count.'
        );
      }

      const { success, error } = await apiPutRequest(
        `${URL}increase-current-user-amount/`,
        {
          new_user_count: new_user_count,
        }
      );

      if (error) {
        throw {
          error: (error as any).data,
          message: 'Error while increasing current user amount',
        };
      }

      await this.getCurrentBillingPeriod();

      return true;
    },
    async changeNextUserAmount(new_user_count: number) {
      // Check if new_user_count is a positive integer
      if (!Number.isInteger(new_user_count) || new_user_count <= 0) {
        throw new Error('New user count must be a positive integer.');
      }

      // No check whether new user count is greater than current user count, because an in- or decrease for the next billing period is allowed

      const { success, error } = await apiPutRequest(
        `${URL}change-next-user-amount/`,
        {
          new_user_count: new_user_count,
        }
      );

      if (error) {
        throw {
          data: (error as any).data,
          message: 'Error while changing next user amount',
        };
      }

      return true;
    },
  },
});
